import { For, Show, createMemo, type Component } from 'solid-js';
import { Text } from '~ui/text';
import { formatPercent } from '~/lib/format';
import { issuerOf, taxFor, type TaxYear } from '~data';

// What each year's distribution was made of, as a grid.
//
// One row per tax year, one column per component, shaded by how much of that
// year's cash it was. The point is the shape: on these funds one cell is almost
// black and the rest are nearly blank, which is a 14% yield that is mostly your
// own capital handed back — visible at a glance in a way a column of percentages
// is not.
//
// A single-hue ramp on the theme's own ink, not a colour scale. The quantity is
// one measure with a natural zero, so lightness alone carries it; a rainbow would
// imply the components are ordered by something other than their share, and any
// fixed hue would fight the series palette, which means *fund* everywhere else.
//
// Every cell is labelled with its number. A ramp is read to about three levels by
// eye, which is enough for "RoC dominates" and nowhere near enough for "72.7% vs
// 83.1%" — and the fund panel's tax section is the exact reading beside it.

const COLUMNS: { label: string; full: string; of: (t: TaxYear) => number }[] = [
    { label: 'RoC', full: 'Return of capital', of: (t) => t.returnOfCapital },
    { label: 'Elig', full: 'Eligible dividends', of: (t) => t.eligibleDividends },
    { label: 'Cap', full: 'Capital gains', of: (t) => t.capitalGains },
    { label: 'Fgn', full: 'Foreign income', of: (t) => t.foreignIncome },
    { label: 'Other', full: 'Other income', of: (t) => t.otherIncome },
    // Withheld at source, so it is a deduction from the cash rather than a part
    // of it — see the reconciliation note in fund-panel. Shaded on the same ramp
    // but read as a negative, which is why it sits last rather than among the
    // components it is netted out of.
    { label: 'FTW', full: 'Foreign tax withheld (deducted)', of: (t) => -t.foreignTaxWithheld },
];

export const TaxHeatmap: Component<{ symbol: string }> = (props) => {
    /** Newest first: what it paid last year is the question. */
    const years = createMemo(() => [...taxFor(props.symbol)].reverse());

    const share = (year: TaxYear, value: number): number | null => (year.totalPerUnit > 0 ? (value / year.totalPerUnit) * 100 : null);

    return (
        <Show
            when={years().length > 0}
            fallback={
                <div class='flex min-h-0 flex-1 items-center justify-center px-2'>
                    <Text tone='muted' class='text-center'>Not published by {issuerOf(props.symbol) ?? 'the issuer'}</Text>
                </div>
            }>
            <div class='min-h-0 flex-1 overflow-auto scrollbar-hide'>
                <table class='w-full table-fixed border-collapse'>
                    <thead>
                        <tr>
                            <th class='h-row w-12 border-b px-1 text-left text-sm font-normal'>
                                <Text tone='muted'>Year</Text>
                            </th>
                            <For each={COLUMNS}>
                                {(c) => (
                                    <th class='h-row border-b px-1 text-center text-sm font-normal' title={c.full}>
                                        <Text tone='muted'>{c.label}</Text>
                                    </th>
                                )}
                            </For>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={years()}>
                            {(year) => (
                                <tr>
                                    <td class='h-row border-b px-1 text-sm'>
                                        <Text weight='medium'>{year.year}</Text>
                                    </td>
                                    <For each={COLUMNS}>
                                        {(c) => {
                                            const value = share(year, c.of(year));
                                            // Magnitude drives the ink, so a
                                            // withheld line reads as strongly as a
                                            // component of the same size. Capped
                                            // short of solid: text has to stay
                                            // legible on top of it.
                                            const weight = value == null ? 0 : Math.min(Math.abs(value) / 100, 1) * 0.8;
                                            return (
                                                <td class='h-row border-b p-0 text-center' title={`${c.full} · ${formatPercent(value, 1)}`}>
                                                    <div class='flex h-full items-center justify-center' style={{ 'background-color': `color-mix(in srgb, var(--color-foreground) ${(weight * 100).toFixed(1)}%, transparent)` }}>
                                                        {/* Flipped to the background ink once the fill is
                                                            dark enough that foreground ink would vanish
                                                            into it. */}
                                                        <span class={`text-sm ${weight > 0.45 ? 'text-background' : value == null || value === 0 ? 'text-muted-foreground' : 'text-foreground'}`}>
                                                            {value == null ? '—' : value === 0 ? '·' : formatPercent(value, 0)}
                                                        </span>
                                                    </div>
                                                </td>
                                            );
                                        }}
                                    </For>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </div>

            <div class='flex h-row shrink-0 items-center border-t px-2'>
                <Text tone='muted' class='truncate'>Share of each year's distribution</Text>
            </div>
        </Show>
    );
};

export default TaxHeatmap;
